/**
 * HectorLink - Rich link component for web sources, case documents, and JP decisions
 *
 * Renders a `<hector-link>` tag from an assistant response as a clickable chip.
 *
 * Two grammars are supported:
 * - **ref (current)**: `<hector-link ref="piece-12" name="Contrat" />`. The token
 *   is resolved against the conversation citation registry (delivered via
 *   `CitationRegistryProvider`); the resolved target is authoritative for the
 *   destination; for JP decisions it also supplies the displayed label.
 * - **inline (legacy)**: `type="web|piece|jp"` with `url` / `file_path` / `ordre`+`id`
 *   written directly by the LLM. Still rendered for pre-ref history.
 *
 * Either way, a target that is not actually usable — an unknown token, a bare
 * Légifrance homepage, a folder path, a `#` — is NOT rendered as a link: it
 * degrades to the plain label as ordinary text, so the lawyer is never sent to a
 * dead/misleading destination.
 */

import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { DocumentViewerService } from '@/services/documentViewerService';
import { DecisionViewerService } from '@/services/decisionViewerService';
import { ErrorService } from '@/lib/errors/ErrorService';
import type { Ordre } from '@hector/shared-types';
import { useCitationTarget, useCitationCaseId, useDestinationCaseHint } from './CitationRegistryContext';
import { isWebUrlUsable, resolvePieceOpening, targetToFields } from './hectorLinkTarget';
import type { LinkFields } from './hectorLinkTarget';

export interface HectorLinkProps {
  /** Citation registry token (`piece-12`, `art-2286-cc`, `jp-1`). Resolved via context. */
  refToken?: string;
  /** Inline type (legacy grammar). Optional when a `refToken` is provided. */
  type?: 'web' | 'piece' | 'jp';
  name: string;
  url?: string;
  file_path?: string;
  /** Required when the resolved/inline type is 'jp'. Re-validated by DecisionViewerService. */
  ordre?: Ordre;
  /** Decision UUID. Required when the resolved/inline type is 'jp'. */
  id?: string;
}

export const HectorLink: React.FC<HectorLinkProps> = ({ refToken, type, name, url, file_path, ordre, id }) => {
  const [isLoading, setIsLoading] = useState(false);
  const target = useCitationTarget(refToken);
  const exchangeCaseId = useCitationCaseId();
  // Suggestion de destination uniquement (cf. `useDestinationCaseHint`) : n'ouvre
  // aucune ressource, sert à pré-remplir « Enregistrer ».
  const destinationCaseHint = useDestinationCaseHint();

  // Effective destination: a resolved registry target is authoritative; otherwise
  // fall back to the legacy inline attributes (pre-ref history). When a `refToken`
  // was given but is unknown, `target` is undefined and there are no legacy attrs
  // → the fields below are empty → renders as plain text.
  const fields: LinkFields = target ? targetToFields(target) : { type, name, url, file_path, ordre, id };
  // Two decisions can share a pourvoi number (e.g. an arrêt and its rectification).
  // The registry label must identify the document we actually open (#2134),
  // even when the model supplies another date in the tag's name.
  const displayName = target?.kind === 'jp' ? target.name : name || fields.name;

  // De quoi ouvrir la pièce, ou `null` si elle ne peut pas s'ouvrir : dans SON
  // dossier quand la citation le porte, sinon dans celui de l'échange
  // (cf. `resolvePieceOpening`).
  const pieceOpening = resolvePieceOpening(fields, displayName, exchangeCaseId);

  // A link is only rendered as a chip if its target is actually resolvable.
  const targetUsable =
    fields.type === 'web' ? isWebUrlUsable(fields.url) :
    fields.type === 'piece' ? pieceOpening !== null :
    fields.type === 'jp' ? (!!fields.ordre && !!fields.id) :
    false;

  const handleClick = async () => {
    if (fields.type === 'web' && fields.url) {
      window.open(fields.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (fields.type === 'jp' && fields.ordre && fields.id) {
      // DecisionViewerService re-validates ordre+id (OrdreSchema + UUID_RE) before opening the panel.
      // Suggestion, pas droit d'accès : le viewer de décision n'est pas scopé
      // cabinet, et ce dossier ne sert qu'à pré-remplir « Enregistrer ». D'où le
      // repli sur celui de la conversation, alors que l'ouverture d'une PIÈCE
      // (plus bas) exige le dossier de la pièce, ou à défaut le `caseId` STRICT
      // du tour.
      //
      // `displayName` est le texte de la puce : il nomme l'onglet tout de suite,
      // au lieu d'un « Arrêt… » anonyme quand on en ouvre plusieurs à la suite.
      // Le registre fournit le titre JP ; l'historique inline garde celui du LLM.
      // Le service assainit les deux.
      DecisionViewerService.viewDecision({
        ordre: fields.ordre,
        id: fields.id,
        caseId: destinationCaseHint,
        title: displayName,
      });
      return;
    }
    if (pieceOpening) {
      setIsLoading(true);
      try {
        await DocumentViewerService.viewDocument(pieceOpening);
      } catch (err) {
        ErrorService.handle(err, { context: { action: 'open_agent_link' } });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Non-usable target → render the label as ordinary text (no chip, no link).
  if (!targetUsable) {
    return <>{displayName}</>;
  }

  // jp reuses the web blue chip style — both are external/navigation actions (blue = "opens something new").
  const isWeb = fields.type === 'web' || fields.type === 'jp';

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        handleClick();
      }}
      disabled={isLoading}
      className={`
        inline italic underline underline-offset-2
        rounded-sm px-0.5 transition-colors duration-150
        ${isWeb
          ? 'text-accent-blue decoration-accent-blue/30 bg-blue-50 ' + (!isLoading ? 'hover:bg-grey-100' : '')
          : 'text-[inherit] decoration-grey-400 bg-grey-200/70 ' + (!isLoading ? 'hover:bg-grey-200/70' : '')
        }
        ${!isLoading ? 'cursor-pointer' : 'cursor-default opacity-60'}
      `}
      style={{ pointerEvents: 'auto' }}
      title={fields.type === 'web' ? fields.url : displayName}
    >
      {isLoading && <Loader2 className="inline h-3 w-3 animate-spin text-grey-400 mr-0.5" />}
      {displayName}
    </button>
  );
};
